import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../core/context/AuthContext";
import { deleteAccount } from "../../core/services/auth";
import { writeConnectorSnapshot } from "../../core/utils/connectorSnapshotCache";
import {
  deleteGlobalActionItemPerformerSchedules,
  getAttioStatus,
  getGlobalActionItemPerformerSchedules,
  getSalesforceStatus,
  patchGlobalActionItemPerformerSchedules,
} from "../businessModeService";
import ActionItemsSettings from "./ActionItemsSettings";
import { useActionItemsSidebar } from "./ActionItemsSidebarContext";
import {
  buildEmptyCrmStatusById,
  getConnectedCrmIdsFromStatus,
  normalizeCrmConnectorStatus,
} from "./shared/crmConnectorStatus";
import {
  ACTION_ITEMS_LIST_PATH,
  ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM,
  ACTION_ITEMS_SETTINGS_PATH,
  ACTION_ITEMS_SETTINGS_TABS,
  ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS,
  ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM,
  ACTION_ITEMS_SETTINGS_TAB_ROOT,
  ACTION_ITEMS_SETTINGS_WORKFLOW_QUERY_PARAM,
  ACTION_ITEMS_SETUP_PROFILE_QUERY_PARAM,
  buildActionItemsListPath,
  clearBusinessOnboardingConnectSetup,
  isActionItemsSetupProfileDeepLink,
  parseActionItemsSettingsApp,
  parseActionItemsSettingsTab,
  parseActionItemsSettingsWorkflow,
  persistManualActionItemsModePreferred,
  pickActionItemsListFilterSearch,
  clearPasteTranscriptHintDismissed,
  tryCloseExtensionOpenedTab,
} from "./actionItemHelpers";
import { isWorkflowSyncingActionItems } from "./shared/workflowStatus";

/** Keep the connectors table in sync without a manual page reload. */
const CONNECTORS_TABLE_REFRESH_INTERVAL_MS = 5 * 60 * 1000;
/** Poll faster while a connector is fetching so Connected/Fetching status stays live. */
const CONNECTORS_SYNCING_REFRESH_INTERVAL_MS = 10 * 1000;

const EMPTY_WORKFLOWS = [];

/**
 * URL params this page owns (everything else in the query string is somebody else's):
 *   settingsTab / settingsApp / settingsWorkflow  deep-link into a tab, an app, a connector
 *   setupProfile                                  highlight the profile step after setup
 *   ext                                           "opened from the extension" marker
 *   code / state / oauth                          OAuth return (triggers one reload)
 * The task-list filter (`workflow`, `category`, ...) is NOT read here.
 */
const SETTINGS_OWNED_QUERY_PARAMS = [
  ACTION_ITEMS_SETTINGS_WORKFLOW_QUERY_PARAM,
  ACTION_ITEMS_SETUP_PROFILE_QUERY_PARAM,
  "ext",
];

/** "oauth" while the URL carries an OAuth return, "" otherwise (changes only at the edges). */
const readOauthReturnKey = (search) => {
  const params = new URLSearchParams(search);
  return params.get("code") || params.get("oauth") ? "oauth" : "";
};

/**
 * Dedicated settings page: shell renders immediately; workflows come from the shared
 * connector store (ActionItemsSidebarProvider), painted from a per-user cache while the
 * live fetch runs in the background.
 *
 * @example
 * <ActionItemsSettingsPage />
 */
const ActionItemsSettingsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    workflows: liveWorkflows,
    setWorkflows,
    workflowsLive,
    cachedWorkflows,
    cachedCrmStatusById,
    refreshWorkflows,
  } = useActionItemsSidebar();
  const mountedRef = useRef(true);
  const userKey = String(user?.id || user?.email || "").trim();

  const setupProfileHighlight = isActionItemsSetupProfileDeepLink(location.search);

  const [connectorsRefreshing, setConnectorsRefreshing] = useState(false);
  const [connectorsLoadFailed, setConnectorsLoadFailed] = useState(false);
  // False until the first live connector fetch of this visit settles. A deep link to a
  // connector that was just created (setup / OAuth return) is not in the cached list yet.
  const [initialConnectorsLoaded, setInitialConnectorsLoaded] = useState(false);
  const [connectedCrmIds, setConnectedCrmIds] = useState(() =>
    cachedCrmStatusById ? getConnectedCrmIdsFromStatus(cachedCrmStatusById) : []
  );
  const [crmStatusById, setCrmStatusById] = useState(
    () => cachedCrmStatusById || buildEmptyCrmStatusById()
  );
  const [performerScheduleSummary, setPerformerScheduleSummary] = useState({
    total: 0,
    enabled: 0,
  });
  const [performerScheduleBusy, setPerformerScheduleBusy] = useState(false);
  const [pageError, setPageError] = useState("");

  // Same list the task list reads (one store), or the persisted snapshot until the first
  // live response lands. Full loading state only when there is neither.
  const workflows = workflowsLive ? liveWorkflows : cachedWorkflows || EMPTY_WORKFLOWS;
  const workflowsLoading = !workflowsLive && !cachedWorkflows && !connectorsLoadFailed;

  const settingsTab = parseActionItemsSettingsTab(location.search);
  const settingsAppFocus = parseActionItemsSettingsApp(location.search);
  // Old links carried the connector focus as `workflow`; honour it on arrival only so a
  // later task-list filter can never steer this page.
  const legacyWorkflowFocusRef = useRef(
    String(new URLSearchParams(location.search).get("workflow") || "").trim()
  );
  const settingsWorkflowFocus =
    parseActionItemsSettingsWorkflow(location.search) || legacyWorkflowFocusRef.current;
  const oauthReturnKey = readOauthReturnKey(location.search);
  // Filter to restore when the user closes settings (handed over by the list page).
  const listSearch = pickActionItemsListFilterSearch(location.state?.listSearch);

  const connectedWorkflowTypes = useMemo(
    () =>
      workflows
        .map((workflow) => String(workflow.workflow_type || "").trim())
        .filter(Boolean),
    [workflows]
  );
  const hasSyncingWorkflow = useMemo(
    () => workflows.some(isWorkflowSyncingActionItems),
    [workflows]
  );

  /**
   * Refresh connector workflow rows without any loading UI.
   *
   * @example
   * await refreshWorkflowsSilent();
   * // shared workflows state updated with latest last_synced_at / job_status
   */
  const refreshWorkflowsSilent = useCallback(async () => {
    try {
      await refreshWorkflows();
    } catch (_) {
      // Keep stale rows on transient failures.
    }
  }, [refreshWorkflows]);

  /**
   * Revalidate everything this screen shows. Existing rows stay on screen (cached or
   * live); only a small "Refreshing" pill appears.
   */
  const loadConnectors = useCallback(async () => {
    setConnectorsRefreshing(true);
    try {
      const [workflowsResult, attioStatus, salesforceStatus] = await Promise.allSettled([
        refreshWorkflows(),
        getAttioStatus().catch(() => ({ connected: false })),
        getSalesforceStatus().catch(() => ({ connected: false })),
      ]);
      if (!mountedRef.current) return;
      if (workflowsResult.status === "rejected") {
        setConnectorsLoadFailed(true);
        setPageError(
          String(workflowsResult.reason?.message || "Failed to load connected apps")
        );
      } else {
        setConnectorsLoadFailed(false);
        setPageError("");
      }
      const nextCrmStatus = {
        attio: normalizeCrmConnectorStatus(
          attioStatus.status === "fulfilled" ? attioStatus.value : { connected: false }
        ),
        salesforce: normalizeCrmConnectorStatus(
          salesforceStatus.status === "fulfilled"
            ? salesforceStatus.value
            : { connected: false }
        ),
      };
      setCrmStatusById(nextCrmStatus);
      setConnectedCrmIds(getConnectedCrmIdsFromStatus(nextCrmStatus));
      if (userKey) writeConnectorSnapshot(userKey, { crmStatusById: nextCrmStatus });
    } finally {
      if (mountedRef.current) {
        setConnectorsRefreshing(false);
        setInitialConnectorsLoaded(true);
      }
    }
  }, [refreshWorkflows, userKey]);

  const loadPerformerScheduleSummary = useCallback(async () => {
    try {
      const data = await getGlobalActionItemPerformerSchedules();
      const summary = data?.summary || {};
      if (!mountedRef.current) return;
      setPerformerScheduleSummary({
        total: Number(summary.total || 0),
        enabled: Number(summary.enabled || 0),
      });
    } catch (_) {
      if (!mountedRef.current) return;
      setPerformerScheduleSummary({ total: 0, enabled: 0 });
    }
  }, []);

  // Load on mount, and again only when an OAuth return starts/ends. Tab, app-focus and
  // task-list filter changes are pure view state and must not refetch anything.
  useEffect(() => {
    mountedRef.current = true;
    void loadConnectors();
    return () => {
      mountedRef.current = false;
    };
  }, [loadConnectors, oauthReturnKey]);

  useEffect(() => {
    void loadPerformerScheduleSummary();
  }, [loadPerformerScheduleSummary]);

  useEffect(() => {
    const intervalMs = hasSyncingWorkflow
      ? CONNECTORS_SYNCING_REFRESH_INTERVAL_MS
      : CONNECTORS_TABLE_REFRESH_INTERVAL_MS;
    const id = setInterval(() => {
      if (!document.hidden) {
        void refreshWorkflowsSilent();
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [hasSyncingWorkflow, refreshWorkflowsSilent]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) {
        void refreshWorkflowsSilent();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [refreshWorkflowsSilent]);

  const handleTogglePerformerSchedules = useCallback(async () => {
    if (performerScheduleBusy || performerScheduleSummary.total === 0) return;
    setPerformerScheduleBusy(true);
    setPageError("");
    try {
      const nextEnabled = performerScheduleSummary.enabled === 0;
      await patchGlobalActionItemPerformerSchedules({ enabled: nextEnabled });
      await loadPerformerScheduleSummary();
    } catch (err) {
      setPageError(String(err?.message || "Failed to update performer schedules"));
    } finally {
      setPerformerScheduleBusy(false);
    }
  }, [loadPerformerScheduleSummary, performerScheduleBusy, performerScheduleSummary]);

  const handleDeletePerformerSchedules = useCallback(async () => {
    if (performerScheduleBusy || performerScheduleSummary.total === 0) return;
    const ok = window.confirm(
      "Remove offline AI performer schedules for all your workflows?"
    );
    if (!ok) return;
    setPerformerScheduleBusy(true);
    setPageError("");
    try {
      await deleteGlobalActionItemPerformerSchedules();
      await loadPerformerScheduleSummary();
    } catch (err) {
      setPageError(String(err?.message || "Failed to delete performer schedules"));
    } finally {
      setPerformerScheduleBusy(false);
    }
  }, [loadPerformerScheduleSummary, performerScheduleBusy, performerScheduleSummary.total]);

  const buildSettingsSearch = useCallback(
    (tab, appFocus = settingsAppFocus) => {
      // Rebuild from the params this page owns; never copy foreign ones (list filter,
      // one-shot OAuth codes) forward.
      const current = new URLSearchParams(location.search);
      const next = new URLSearchParams();
      SETTINGS_OWNED_QUERY_PARAMS.forEach((key) => {
        const value = current.get(key);
        if (value) next.set(key, value);
      });
      if (tab !== ACTION_ITEMS_SETTINGS_TAB_ROOT) {
        next.set(ACTION_ITEMS_SETTINGS_TAB_QUERY_PARAM, tab);
      }
      if (tab === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS && appFocus) {
        next.set(ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM, appFocus);
      } else {
        next.delete(ACTION_ITEMS_SETTINGS_APP_QUERY_PARAM);
      }
      const qs = next.toString();
      return `${ACTION_ITEMS_SETTINGS_PATH}${qs ? `?${qs}` : ""}`;
    },
    [location.search, settingsAppFocus]
  );

  const handleSettingsTabChange = useCallback(
    (nextTab) => {
      const resolvedTab = ACTION_ITEMS_SETTINGS_TABS.includes(nextTab)
        ? nextTab
        : ACTION_ITEMS_SETTINGS_TAB_ROOT;
      const appFocus =
        resolvedTab === ACTION_ITEMS_SETTINGS_TAB_CONNECT_APPS ? settingsAppFocus : "";
      // Carry router state so the list filter survives tab hops.
      navigate(buildSettingsSearch(resolvedTab, appFocus), { state: location.state });
    },
    [buildSettingsSearch, location.state, navigate, settingsAppFocus]
  );

  const handlePasteTranscriptInstead = useCallback(() => {
    persistManualActionItemsModePreferred();
    clearPasteTranscriptHintDismissed();
    const filter = new URLSearchParams(listSearch);
    navigate(
      buildActionItemsListPath({
        workflow: filter.get("workflow") || "",
        category: filter.get("category") || "",
        pasteTranscript: true,
      })
    );
  }, [listSearch, navigate]);

  const handleCloseSettings = useCallback(() => {
    navigate(`${ACTION_ITEMS_LIST_PATH}${listSearch}`);
    tryCloseExtensionOpenedTab();
  }, [listSearch, navigate]);

  const handleDeleteAccount = useCallback(async () => {
    await deleteAccount();
  }, []);

  const handleCrmStatusChange = useCallback((crmId, nextStatus) => {
    const provider = String(crmId || "").trim();
    if (!provider) return;
    setCrmStatusById((previous) => {
      const next = {
        ...previous,
        [provider]: normalizeCrmConnectorStatus(nextStatus),
      };
      setConnectedCrmIds(getConnectedCrmIdsFromStatus(next));
      return next;
    });
  }, []);

  return (
    <div className="business-page-content">
      {connectorsRefreshing && !workflowsLoading ? (
        <div
          className="connectors-refresh-pill"
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: 12,
            right: 16,
            zIndex: 20,
            padding: "4px 10px",
            borderRadius: 999,
            fontSize: 12,
            lineHeight: "16px",
            background: "rgba(0, 0, 0, 0.06)",
            color: "inherit",
            pointerEvents: "none",
          }}
        >
          Refreshing…
        </div>
      ) : null}
      {pageError ? <div className="error-message">{pageError}</div> : null}
      <ActionItemsSettings
        layout="page"
        userName={user?.name}
        userEmail={user?.email}
        setupProfileHighlight={setupProfileHighlight}
        onClose={handleCloseSettings}
        pendingItems={[]}
        aiResultsByItem={{}}
        performerScheduleSummary={performerScheduleSummary}
        onTogglePerformerSchedules={handleTogglePerformerSchedules}
        onDeletePerformerSchedules={handleDeletePerformerSchedules}
        performerScheduleBusy={performerScheduleBusy}
        activeTab={settingsTab}
        onTabChange={handleSettingsTabChange}
        connectAppFocus={settingsAppFocus}
        connectWorkflowFocus={settingsWorkflowFocus}
        connectFocusPending={!initialConnectorsLoaded}
        connectedWorkflowTypes={connectedWorkflowTypes}
        connectedCrmIds={connectedCrmIds}
        crmStatusById={crmStatusById}
        onCrmStatusChange={handleCrmStatusChange}
        workflows={workflows}
        workflowsLoading={workflowsLoading}
        connectAppsVariant="web"
        onNavigateToSetup={(setupPath) => {
          clearBusinessOnboardingConnectSetup();
          navigate(String(setupPath || "").trim());
        }}
        onOpenWorkflow={(workflowId) =>
          navigate(`/business/workflows/${String(workflowId || "").trim()}`)
        }
        onWorkflowsChange={setWorkflows}
        onDeleteAccount={handleDeleteAccount}
        onPasteTranscriptInstead={handlePasteTranscriptInstead}
      />
    </div>
  );
};

export default ActionItemsSettingsPage;
